<script setup>
// Addendum §1 DropdownMenu — hand-rolled to the same bar Reka's would set:
// keyboard nav (Up/Down/Home/End), typeahead, focus management, Esc, outside
// click to close, and focus RETURNED to the trigger on close. Roles: the
// trigger carries aria-haspopup="menu" + aria-expanded; the popup is role=menu;
// items are role=menuitem.
//
// items: [{ label, icon?, destructive?, disabled?, onSelect } | { separator:true }]
// Destructive items are never first and always sit after a separator (§2.4) —
// that's the caller's ordering; this component renders whatever it's given.
import { ref, nextTick, onBeforeUnmount, computed } from 'vue'
import LIcon from '../base/Icon.vue'

const props = defineProps({
	items: { type: Array, default: () => [] },
	align: { type: String, default: 'start' }, // start | end
	// Which side of the trigger the menu opens on. "top" is for triggers that sit
	// at the bottom of their container (the sidebar user menu), where a menu
	// dropping down would fall off the frame.
	placement: { type: String, default: 'bottom' }, // bottom | top
})

const open = ref(false)
const root = ref(null)
const menuEl = ref(null)
const triggerRef = ref(null)
let typeahead = ''
let typeaheadTimer = null

const actionable = computed(() => props.items.filter((i) => !i.separator))

function menuItems()
{
	return menuEl.value ? [...menuEl.value.querySelectorAll('[role="menuitem"]:not([disabled])')] : []
}
function focusIndex(i)
{
	const els = menuItems()
	if (!els.length) return
	const idx = (i + els.length) % els.length
	els[idx]?.focus()
}

async function openMenu(focusLast = false)
{
	open.value = true
	document.addEventListener('pointerdown', onDocPointer, true)
	await nextTick()
	focusIndex(focusLast ? -1 : 0)
}
function closeMenu(returnFocus = true)
{
	open.value = false
	document.removeEventListener('pointerdown', onDocPointer, true)
	if (returnFocus) triggerRef.value?.focus?.()
}
function toggle()
{
	open.value ? closeMenu() : openMenu()
}
function onDocPointer(e)
{
	if (root.value && !root.value.contains(e.target)) closeMenu(false)
}
onBeforeUnmount(() => document.removeEventListener('pointerdown', onDocPointer, true))

function select(it)
{
	if (it.disabled) return
	it.onSelect?.()
	closeMenu()
}

function onTriggerKey(e)
{
	if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')
	{
		e.preventDefault()
		openMenu(false)
	} else if (e.key === 'ArrowUp')
	{
		e.preventDefault()
		openMenu(true)
	}
}
function onMenuKey(e)
{
	const els = menuItems()
	const current = els.indexOf(document.activeElement)
	switch (e.key)
	{
		case 'ArrowDown': e.preventDefault(); focusIndex(current + 1); break
		case 'ArrowUp': e.preventDefault(); focusIndex(current - 1); break
		case 'Home': e.preventDefault(); focusIndex(0); break
		case 'End': e.preventDefault(); focusIndex(-1); break
		case 'Escape': e.preventDefault(); closeMenu(); break
		case 'Tab': closeMenu(false); break
		default:
			if (e.key.length === 1 && /\S/.test(e.key))
			{
				typeahead += e.key.toLowerCase()
				clearTimeout(typeaheadTimer)
				typeaheadTimer = setTimeout(() => (typeahead = ''), 500)
				const match = els.findIndex((el) => el.textContent.trim().toLowerCase().startsWith(typeahead))
				if (match >= 0) els[match].focus()
			}
	}
}

// expose to composing components (SplitButton / MenuButton)
defineExpose({ open, toggle, openMenu, closeMenu })
</script>

<template>
	<div ref="root" class="l-dd">
		<span ref="triggerRef">
			<slot name="trigger" :toggle="toggle" :open="open" :onkeydown="onTriggerKey" />
		</span>

		<div v-if="open" ref="menuEl" class="menu" :class="[`align-${align}`, `place-${placement}`]" role="menu"
			@keydown="onMenuKey">
			<template v-for="(it, i) in items" :key="i">
				<div v-if="it.separator" class="sep" role="separator" />
				<button v-else type="button" role="menuitem" tabindex="-1" class="item"
					:class="{ destructive: it.destructive }" :disabled="it.disabled" @click="select(it)">
					<LIcon v-if="it.icon" :name="it.icon" :size="13" class="item-icon" />
					<span class="item-label">{{ it.label }}</span>
				</button>
			</template>
		</div>
	</div>
</template>

<style scoped>
.l-dd
{
	position: relative;
	display: inline-flex;
}

.menu
{
	position: absolute;
	min-width: 200px;
	background: var(--surface);
	border: 1px solid var(--line);
	border-radius: var(--r);
	box-shadow: var(--shadow);
	padding: 5px;
	z-index: 70;
}

.align-start
{
	left: 0;
}

.align-end
{
	right: 0;
}

.place-bottom
{
	top: calc(100% + 6px);
}

.place-top
{
	bottom: calc(100% + 6px);
}

.item
{
	display: flex;
	align-items: center;
	gap: var(--sp-2);
	width: 100%;
	text-align: left;
	font-size: var(--text-base);
	color: var(--text);
	padding: 8px 10px;
	border-radius: var(--r-sm);
	min-height: var(--target-min);
}

.item:hover:not(:disabled),
.item:focus-visible
{
	background: var(--surface-sunken);
}

.item:disabled
{
	opacity: 0.45;
	cursor: not-allowed;
}

.item.destructive
{
	color: var(--status-rejected-text);
}

.item.destructive:hover:not(:disabled),
.item.destructive:focus-visible
{
	background: var(--status-rejected-fill);
}

.item-icon
{
	color: var(--icon);
	width: 15px;
	text-align: center;
}

.item.destructive .item-icon
{
	color: var(--status-rejected-text);
}

.sep
{
	height: 1px;
	background: var(--line-soft);
	margin: 5px 0;
}
</style>
